<template>
  <b-modal
    id="pm-share"
    v-model="open"
    :title="$t('partner-map.share.title')"
    size="lg"
    ok-only
    :ok-title="$t('partner-map.share.close')"
    @show="load"
  >
    <p class="text-muted small">{{ $t('partner-map.share.intro') }}</p>

    <b-alert v-if="error" show variant="danger" class="small">
      {{ error }}
    </b-alert>

    <div v-if="loading" class="text-center py-3">
      <b-spinner small />
    </div>

    <b-alert
      v-else-if="!recipients.length"
      show
      variant="warning"
      class="small"
    >
      {{ $t('partner-map.share.no-recipients') }}
    </b-alert>

    <template v-else>
      <b-form-group :label="$t('partner-map.share.recipient')">
        <b-form-select v-model="selected" :options="recipientOptions" />
      </b-form-group>

      <b-button
        variant="primary"
        size="sm"
        :disabled="!selected || minting"
        @click="mint"
      >
        <b-spinner v-if="minting" small class="mr-1" />
        {{ $t('partner-map.share.create') }}
      </b-button>

      <div v-if="link" class="pm-share-result mt-3">
        <label class="small text-muted mb-1">
          {{ $t('partner-map.share.link-for', { email: link.email }) }}
        </label>
        <b-input-group size="sm">
          <b-form-input
            :value="link.url"
            readonly
            @focus="$event.target.select()"
          />
          <b-input-group-append>
            <b-button variant="outline-secondary" @click="copy">
              {{
                copied
                  ? $t('partner-map.share.copied')
                  : $t('partner-map.share.copy')
              }}
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <p class="small text-muted mt-2 mb-0">
          {{ $t('partner-map.share.expires', { date: expiresLabel }) }}
        </p>
        <b-alert show variant="warning" class="small mt-2 mb-0">
          {{ $t('partner-map.share.warning') }}
        </b-alert>
      </div>
    </template>
  </b-modal>
</template>

<script>
import Api from '@gid/vue-common/api';

/**
 * Mints a pre-authenticated link to the partner map for a holding contact.
 *
 * The recipient list comes from the server and contains only contacts confined
 * to a holding profile — the endpoint refuses to mint for anybody else, because
 * the link logs its holder in as that contact. This component deliberately does
 * not offer free-text entry of an address for the same reason.
 */
export default {
  name: 'PartnerMapShare',
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      open: this.value,
      loading: false,
      minting: false,
      error: null,
      recipients: [],
      selected: null,
      link: null,
      copied: false,
    };
  },
  computed: {
    recipientOptions() {
      return this.recipients.map((r) => ({
        value: r.email,
        text: r.name ? `${r.name} — ${r.email}` : r.email,
      }));
    },
    expiresLabel() {
      if (!this.link) return '';
      return this.$moment(this.link.expires_at).format('LLL');
    },
  },
  watch: {
    value(v) {
      this.open = v;
    },
    open(v) {
      this.$emit('input', v);
      if (!v) {
        // Never leave a live credential sitting in a closed modal.
        this.link = null;
        this.copied = false;
      }
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      this.link = null;
      try {
        const { data } = await Api.get(
          '/api/admin/partners/map/share-recipients',
        );
        this.recipients = data.recipients || [];
        this.selected = this.recipients.length
          ? this.recipients[0].email
          : null;
      } catch (e) {
        this.error = e.response?.data?.msg || e.message;
        this.recipients = [];
      } finally {
        this.loading = false;
      }
    },
    async mint() {
      this.minting = true;
      this.error = null;
      this.copied = false;
      try {
        const { data } = await Api.post('/api/admin/partners/map/share', {
          email: this.selected,
        });
        this.link = data;
      } catch (e) {
        this.error = e.response?.data?.msg || e.message;
        this.link = null;
      } finally {
        this.minting = false;
      }
    },
    async copy() {
      try {
        await navigator.clipboard.writeText(this.link.url);
        this.copied = true;
      } catch (e) {
        this.error = this.$t('partner-map.share.copy-failed');
      }
    },
  },
};
</script>

<style scoped>
.pm-share-result {
  border-top: 1px solid rgba(128, 128, 128, 0.25);
  padding-top: 12px;
}
</style>
